/**
 * Gestión → Clientes (`/gestion/clientes`).
 *
 * Puerto de `Clients::index` + `views/menu/cliente/index.php` y del bundle
 * `assets/js/clients.bundle.js`: el listado de clientes con sus **placas**, el
 * estado del **portal**, el estado y la condición SUNAT, la **retención** y el
 * acceso a la ficha.
 *
 * El panel anterior mostraba nueve columnas de DataTables y un menú desplegable
 * por fila con seis acciones (Placas/Descuentos, Editar, Regenerar contraseña,
 * Desactivar portal, Activar portal y Eliminar). Aquí las tres últimas —con la
 * edición y la baja— viven en la **ficha del cliente**, y la fila se queda con un
 * único botón para abrirla, de modo que la tabla no se llene de botones.
 *
 * Lo que el panel anterior hacía en la cabecera y sigue **pendiente** de migrar
 * (se documenta al pie de la pantalla): sincronizar placas con `customer_vehicles`
 * (sólo aplica al modo APP), actualizar los precios de descuento, administrar los
 * tipos de cliente por placa y el alta con consulta a SUNAT.
 */

import {
  Car,
  ChevronLeft,
  ChevronRight,
  Info,
  KeyRound,
  Lock,
  Pencil,
  RefreshCw,
  Search,
  ShieldCheck,
  TrendingUp,
  UserRound,
  UserRoundPlus,
  Truck,
  Users,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

import { ClientePlacasDialog } from '../components/cliente-placas-dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { Link } from 'react-router-dom';

import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { cn } from '@/lib/utils';

import { useClientes } from '../clientes.api';
import { ClienteFichaDialog } from '../components/cliente-ficha-dialog';
import { ClienteNuevoDialog } from '../components/cliente-nuevo-dialog';
import type { Cliente, FiltrosClientes } from '../gestion.types';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Tamano de página del listado. */
const TAMANOS = [25, 50, 100];

/** Fecha del panel anterior (`d/m/Y`), o un guion. */
function fechaCorta(valor: string | null): string {
  if (!valor) return '—';

  const fecha = new Date(valor);

  if (Number.isNaN(fecha.getTime())) return valor;

  return fecha.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Tarjeta del resumen de la cabecera. */
function Resumen({
  icono: Icono,
  etiqueta,
  valor,
  detalle,
  tono,
}: {
  icono: typeof Users;
  etiqueta: string;
  valor: number;
  detalle: string;
  tono: string;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 p-4">
        <span className={cn('grid size-10 shrink-0 place-items-center rounded-control', tono)}>
          <Icono className="size-5" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">{etiqueta}</p>
          <p className="text-lg font-bold text-ink-900">{valor.toLocaleString('es-PE')}</p>
          <p className="line-clamp-2 text-xs text-ink-500">{detalle}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export function ClientesPage() {
  useDocumentTitle('Gestión · Clientes');

  const [texto, setTexto] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [estado, setEstado] = useState('1');
  const [portal, setPortal] = useState('');
  const [placas, setPlacas] = useState('');
  const [orden, setOrden] = useState('nombre');
  const [dir, setDir] = useState<'asc' | 'desc'>('asc');
  const [pagina, setPagina] = useState(0);
  const [limite, setLimite] = useState(25);
  const [ficha, setFicha] = useState<{ id: number; editar: boolean } | null>(null);
  const [placasDialog, setPlacasDialog] = useState<{
    id: number;
    nombre: string | null;
    numero_doc: string | null;
  } | null>(null);
  const [nuevo, setNuevo] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);

  // La búsqueda libre espera a que el usuario deje de escribir.
  useEffect(() => {
    const temporizador = setTimeout(() => {
      setBusqueda(texto.trim());
      setPagina(0);
    }, 300);

    return () => clearTimeout(temporizador);
  }, [texto]);

  const filtros = useMemo<FiltrosClientes>(
    () => ({
      search: busqueda,
      estado,
      portal,
      placas,
      orden,
      dir,
      limit: limite,
      offset: pagina * limite,
    }),
    [busqueda, estado, portal, placas, orden, dir, limite, pagina],
  );

  const consulta = useClientes(filtros);
  const listado = consulta.data;
  const clientes = listado?.clientes ?? [];
  const filtrados = listado?.filtrados ?? 0;
  const resumen = listado?.resumen;
  const totalPaginas = Math.max(1, Math.ceil(filtrados / limite));

  /** Ordena por una columna (alterna el sentido si ya estaba activa). */
  const ordenar = (columna: string) => {
    if (orden === columna) {
      setDir(dir === 'asc' ? 'desc' : 'asc');

      return;
    }

    setOrden(columna);
    setDir('asc');
  };

  const flecha = (columna: string) => (orden === columna ? (dir === 'asc' ? '▲' : '▼') : '');

  const mostrar = (mensaje: string) => {
    setAviso(mensaje);
    setTimeout(() => setAviso(null), 6000);
  };

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">Clientes</h1>
          <p className="mt-1 text-sm text-ink-500">
            Padrón de clientes con sus placas y el estado del portal web.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            leftIcon={
              <RefreshCw
                className={cn('size-4', consulta.isFetching && 'animate-spin')}
                aria-hidden="true"
              />
            }
            onClick={() => void consulta.refetch()}
          >
            Actualizar
          </Button>
          <Button
            size="sm"
            leftIcon={<UserRoundPlus className="size-4" />}
            onClick={() => setNuevo(true)}
          >
            Nuevo cliente
          </Button>
        </div>
      </header>

      {aviso ? (
        <Alert variant="success" title="Listo">
          <span className={ICONO_EN_ALERTA}>
            <ShieldCheck className="size-4" aria-hidden="true" />
            {aviso}
          </span>
        </Alert>
      ) : null}

      {resumen ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
          <Resumen
            icono={Users}
            etiqueta="Clientes"
            valor={resumen.total}
            detalle={`${resumen.activos} habilitados · ${resumen.inhabilitados} inhabilitados`}
            tono="bg-brand-50 text-brand-600"
          />
          <Resumen
            icono={KeyRound}
            etiqueta="Con portal"
            valor={resumen.con_portal}
            detalle="Con acceso al portal web"
            tono="bg-emerald-50 text-emerald-600"
          />
          <Resumen
            icono={Car}
            etiqueta="Con placas"
            valor={resumen.con_placas}
            detalle="Con al menos una placa activa"
            tono="bg-amber-50 text-amber-600"
          />
          <Resumen
            icono={TrendingUp}
            etiqueta="Con retención"
            valor={resumen.con_retencion}
            detalle="RUC que aplican retenciones"
            tono="bg-rose-50 text-rose-600"
          />
          <Resumen
            icono={UserRound}
            etiqueta="Filtrados"
            valor={filtrados}
            detalle="Resultados con los filtros actuales"
            tono="bg-ink-100 text-ink-600"
          />
        </div>
      ) : null}

      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="grid items-end gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <label className="flex flex-col gap-1 text-xs text-ink-500 sm:col-span-2 xl:col-span-2">
              Buscar cliente
              <span className="relative block">
              <Search
                className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-400"
                aria-hidden="true"
              />
              <input
                className={cn(CONTROL, 'pl-9')}
                placeholder="Buscar por nombre, documento o dirección…"
                value={texto}
                onChange={(evento) => setTexto(evento.target.value)}
              />
                        </span>
            </label>

            <label className="flex flex-col gap-1 text-xs text-ink-500">
              Estado
              <span className="relative block">
              <Select
                className={CONTROL}
                value={estado}
                onChange={(evento) => {
                  setEstado(evento.target.value);
                  setPagina(0);
                }}
              >
                <option value="1">Habilitados</option>
                <option value="0">Inhabilitados</option>
                <option value="todos">Todos los estados</option>
              </Select>
                        </span>
            </label>

            <label className="flex flex-col gap-1 text-xs text-ink-500">
              Portal
              <span className="relative block">
              <Select
                className={CONTROL}
                value={portal}
                onChange={(evento) => {
                  setPortal(evento.target.value);
                  setPagina(0);
                }}
              >
                <option value="">Portal: todos</option>
                <option value="1">Con portal activo</option>
                <option value="0">Sin portal</option>
              </Select>
                        </span>
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-ink-500">
              Placas
              <Select
                className="h-9 rounded-control border border-ink-200 bg-white px-2 text-xs text-ink-800"
                value={placas}
                onChange={(evento) => {
                  setPlacas(evento.target.value);
                  setPagina(0);
                }}
              >
                <option value="">todas</option>
                <option value="con">con placas</option>
                <option value="sin">sin placas</option>
              </Select>
            </label>

            <label className="flex items-center gap-2 text-xs text-ink-500">
              Orden
              <Select
                className="h-9 rounded-control border border-ink-200 bg-white px-2 text-xs text-ink-800"
                value={orden}
                onChange={(evento) => setOrden(evento.target.value)}
              >
                <option value="nombre">nombre</option>
                <option value="id">#</option>
                <option value="created_at">alta</option>
                <option value="portal_activo">portal</option>
              </Select>
              <Select
                className="h-9 rounded-control border border-ink-200 bg-white px-2 text-xs text-ink-800"
                value={dir}
                onChange={(evento) => setDir(evento.target.value as 'asc' | 'desc')}
              >
                <option value="asc">ascendente</option>
                <option value="desc">descendente</option>
              </Select>
            </label>

            <label className="flex items-center gap-2 text-xs text-ink-500">
              Filas
              <Select
                className="h-9 rounded-control border border-ink-200 bg-white px-2 text-xs text-ink-800"
                value={String(limite)}
                onChange={(evento) => {
                  setLimite(Number(evento.target.value));
                  setPagina(0);
                }}
              >
                {TAMANOS.map((tamano) => (
                  <option key={tamano} value={String(tamano)}>
                    {tamano}
                  </option>
                ))}
              </Select>
            </label>

            <span className="ml-auto text-xs text-ink-500">
              {filtrados.toLocaleString('es-PE')} cliente{filtrados === 1 ? '' : 's'}
            </span>
          </div>

          {consulta.isError ? (
            <Alert variant="error" title="No se pudo cargar el listado">
              {consulta.error instanceof ApiError
                ? consulta.error.message
                : 'Inténtalo de nuevo en unos segundos.'}
            </Alert>
          ) : null}

          <div className="max-h-[60vh] overflow-auto rounded-card border border-ink-100">
            <table className="w-full border-collapse text-[13px]">
              <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                  <th className="px-2 py-2">
                    <button className="hover:text-brand-600" onClick={() => ordenar('id')}>
                      Cliente {flecha('id')}
                    </button>
                  </th>
                  <th className="px-2 py-2">Dirección</th>
                  <th className="px-2 py-2">Placas</th>
                  <th className="px-2 py-2" title="Estado del cliente y su estado y condición en SUNAT">
                    Estado / SUNAT
                  </th>
                  <th className="px-2 py-2">
                    <button className="hover:text-brand-600" onClick={() => ordenar('portal_activo')}>
                      Portal {flecha('portal_activo')}
                    </button>
                  </th>
                  <th className="px-2 py-2 text-right">Acciones</th>
                </tr>
              </thead>

              <tbody>
                {consulta.isLoading
                  ? Array.from({ length: 6 }).map((_, indice) => (
                      <tr key={`esqueleto-${indice}`} className="border-t border-ink-100">
                        {Array.from({ length: 6 }).map((__, columna) => (
                          <td key={`celda-${columna}`} className="px-2 py-3">
                            <Skeleton className="h-4 w-full" />
                          </td>
                        ))}
                      </tr>
                    ))
                  : null}

                {!consulta.isLoading && clientes.length === 0 ? (
                  <tr className="border-t border-ink-100">
                    <td colSpan={6} className="px-3 py-10 text-center text-sm text-ink-500">
                      No hay clientes que cumplan los filtros.
                    </td>
                  </tr>
                ) : null}

                {clientes.map((cliente: Cliente) => (
                  <tr key={cliente.id} className="border-t border-ink-100 hover:bg-brand-50/30">
                    <td className="max-w-[10rem] px-2 py-2">
                      <p className="truncate font-semibold text-ink-900" title={cliente.nombre ?? ''}>
                        {cliente.nombre ?? '—'}
                      </p>
                      <p className="truncate text-xs text-ink-500">
                        {cliente.tipo_doc === '6' ? 'RUC' : 'DNI'} {cliente.numero_doc ?? ''}
                        {cliente.withholding ? ` · retención ${cliente.withholding_percentage}%` : ''}
                      </p>
                    </td>

                    <td className="max-w-[11rem] px-2 py-2 text-xs text-ink-600">
                      <p className="truncate" title={cliente.direccion ?? ''}>
                        {cliente.direccion || '—'}
                      </p>
                    </td>

                    <td className="px-2 py-2 whitespace-nowrap">
                      {cliente.cantidad_placas === 0 ? (
                        <span className="text-xs text-ink-400">Sin placas</span>
                      ) : (
                        <button
                          className="inline-flex items-center gap-1.5 rounded-control border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-800 hover:border-amber-300"
                          title={cliente.placas.map((placa) => placa.numero_placa).join(', ')}
                          onClick={() => setFicha({ id: cliente.id, editar: false })}
                        >
                          <Car className="size-3.5" aria-hidden="true" />
                          {cliente.cantidad_placas}
                        </button>
                      )}
                    </td>

                    <td className="px-2 py-2 whitespace-nowrap">
                      <Badge variant={cliente.estado ? 'success' : 'neutral'}>
                        {cliente.estado ? 'HABILITADO' : 'INHABILITADO'}
                      </Badge>
                      <p className="text-xs text-ink-500">
                        {cliente.estado_sunat || cliente.condicion_sunat
                          ? `${cliente.estado_sunat ?? '—'} · ${cliente.condicion_sunat ?? '—'}`
                          : 'sin datos SUNAT'}
                      </p>
                    </td>

                    <td className="px-2 py-2 whitespace-nowrap">
                      {cliente.portal_activo ? (
                        <>
                          <Badge variant="brand">Activo</Badge>
                          <p className="text-xs text-ink-500">
                            {cliente.portal_ultimo_acceso
                              ? fechaCorta(cliente.portal_ultimo_acceso)
                              : 'sin accesos'}
                          </p>
                        </>
                      ) : (
                        <span className="text-xs text-ink-400">Sin portal</span>
                      )}
                    </td>

                    <td className="px-2 py-2">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Placas y descuentos del cliente"
                          aria-label={`Placas de ${cliente.nombre ?? `cliente #${cliente.id}`}`}
                          onClick={() =>
                            setPlacasDialog({
                              id: cliente.id,
                              nombre: cliente.nombre,
                              numero_doc: cliente.numero_doc,
                            })
                          }
                        >
                          <Truck className="size-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          title="Editar la ficha del cliente"
                          aria-label={`Editar ${cliente.nombre ?? `el cliente #${cliente.id}`}`}
                          onClick={() => setFicha({ id: cliente.id, editar: true })}
                        >
                          <Pencil className="size-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          title="Abrir ficha del cliente"
                          aria-label={`Abrir la ficha de ${cliente.nombre ?? `cliente #${cliente.id}`}`}
                          onClick={() => setFicha({ id: cliente.id, editar: false })}
                        >
                          <UserRound className="size-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs text-ink-500">
              Página {pagina + 1} de {totalPaginas.toLocaleString('es-PE')}
              {filtrados > 0
                ? ` · mostrando ${(pagina * limite + 1).toLocaleString('es-PE')}–${Math.min(
                    (pagina + 1) * limite,
                    filtrados,
                  ).toLocaleString('es-PE')}`
                : ''}
            </span>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pagina === 0}
                leftIcon={<ChevronLeft className="size-4" />}
                onClick={() => setPagina((actual) => Math.max(0, actual - 1))}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={pagina + 1 >= totalPaginas}
                rightIcon={<ChevronRight className="size-4" />}
                onClick={() => setPagina((actual) => actual + 1)}
              >
                Siguiente
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Alert variant="info" title="Pendiente de migrar en Clientes">
        <ul className="mt-1 ml-5 list-disc space-y-1 text-sm">
          <li>
            <span className={ICONO_EN_ALERTA}>
              <Lock className="size-3.5" aria-hidden="true" />
              Sincronización de placas con `customer_vehicles`, que sólo aplica al modo APP.
            </span>
          </li>
        </ul>
      </Alert>

      <p className="flex items-start gap-2 text-xs text-ink-500">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        <span>
          La edición, las acciones del portal y la baja del cliente están en su ficha, no en la fila
          (el menú desplegable del panel anterior tenía seis acciones por cliente). Los{' '}
          <Link
            className="font-semibold text-brand-700 underline"
            to="/gestion/clientes/saldos"
          >
            créditos y vales
          </Link>{' '}
          del cliente, sus placas y tipos de placa tienen su propia pantalla, y el alta ya consulta el
          documento en SUNAT/RENIEC con el botón *Buscar*.
        </span>
      </p>

      <ClienteFichaDialog
        key={ficha ? `${ficha.id}-${ficha.editar ? 'editar' : 'ver'}` : 'sin-cliente'}
        id={ficha?.id ?? null}
        open={ficha !== null}
        editarInicial={ficha?.editar ?? false}
        onClose={() => setFicha(null)}
        onAviso={mostrar}
      />

      {nuevo ? (
        <ClienteNuevoDialog onClose={() => setNuevo(false)} onAviso={mostrar} />
      ) : null}
      <ClientePlacasDialog
        cliente={placasDialog}
        onClose={() => setPlacasDialog(null)}
        onAviso={mostrar}
      />

    </div>
  );
}
